import {Box, Button, Flex, IconButton, Spinner, Text, Tooltip} from "@chakra-ui/react";
import {useEffect, useMemo, useRef, useState} from "react";
import {FiChevronLeft, FiChevronRight} from "react-icons/fi";
import {
  WEEKDAYS,
  addDays,
  addMonths,
  endOfWeek,
  longDay,
  monthOf,
  monthTitle,
  monthWeeks,
  sameDayInMonth,
  startOfWeek,
} from "../../services/schedule/monthGrid";
import {closedCopyFor, dayStatus} from "../../services/schedule/rescheduleOptions";

// The month a visit can move to, on the CLINIC's calendar.
//
// It replaces a native date field, which wrote the date in the browser's
// format ("14/10/2026" beside text that says "October 14") and let a patient
// learn a day was empty only after picking it. Here every day says what it
// holds before it is chosen: a day with times is marked and selectable; a day
// without says why — on hover, on keyboard focus, in its accessible name, and
// in a line under the grid when it is tapped (a phone has no hover).
//
// Keyboard, as a date grid is expected to behave: arrows move a day or a week,
// Home and End go to the week's ends, Page Up and Page Down change month
// (with Shift, year); Enter or Space picks. One day is in the tab order.
//
// Behaviour mirrors the mobile app's reschedule sheet (blhPatientMobile
// AppointmentRescheduleSheet.tsx): days without times are not choosable, the
// visit's current day is outlined as a reference, months before today's are
// not offered.

function Mark({kind, onSelected}) {
  if (kind === "open") {
    return <Box aria-hidden="true" w="5px" h="5px" borderRadius="full" bg={onSelected ? "white" : "blue.500"} />;
  }
  if (kind === "full") {
    return <Box aria-hidden="true" w="8px" h="2px" borderRadius="full" bg="gray.300" />;
  }
  return <Box aria-hidden="true" h="5px" />;
}

export default function RescheduleCalendar({
  monthKey,
  minMonthKey,
  onMonthChange,
  todayKey,
  days,
  loadedMonths,
  did,
  selectedKey,
  onSelect,
  onClosedDay,
  currentKey,
  loading,
}) {
  const [focusedKey, setFocusedKey] = useState(null);
  const cellRefs = useRef(new Map());
  const pendingFocus = useRef(null);
  const loaded = loadedMonths.includes(monthKey);
  const weeks = useMemo(() => monthWeeks(monthKey), [monthKey]);
  const titleId = `reschedule-month-${monthKey}`;

  const statusOf = (key) => dayStatus(days?.[key], {did, isPast: key < todayKey, loaded});

  // The one day in the tab order: the focused one, else the chosen one, else
  // the first with times, else the first not yet past.
  const tabbableKey = useMemo(() => {
    const inMonth = (key) => key && monthOf(key) === monthKey;
    if (inMonth(focusedKey)) return focusedKey;
    if (inMonth(selectedKey)) return selectedKey;
    const all = weeks.flat().filter(Boolean);
    return (
      all.find((key) => dayStatus(days?.[key], {did, isPast: key < todayKey}).open) ||
      all.find((key) => key >= todayKey) ||
      all[0]
    );
  }, [focusedKey, selectedKey, monthKey, weeks, days, did, todayKey]);

  // Focus follows the keyboard into a month that has just been drawn.
  useEffect(() => {
    const key = pendingFocus.current;
    if (!key) return;
    const cell = cellRefs.current.get(key);
    if (cell) {
      cell.focus();
      pendingFocus.current = null;
    }
  });

  const moveTo = (target) => {
    if (monthOf(target) < minMonthKey) return;
    if (monthOf(target) !== monthKey) onMonthChange(monthOf(target));
    pendingFocus.current = target;
    setFocusedKey(target);
  };

  const pick = (key) => {
    const status = statusOf(key);
    if (status.open) onSelect(key);
    else if (status.reason) onClosedDay(key, status.reason);
  };

  const onKeyDown = (event, key) => {
    const moves = {
      ArrowRight: () => addDays(key, 1),
      ArrowLeft: () => addDays(key, -1),
      ArrowDown: () => addDays(key, 7),
      ArrowUp: () => addDays(key, -7),
      Home: () => startOfWeek(key),
      End: () => endOfWeek(key),
      PageDown: () => sameDayInMonth(key, event.shiftKey ? 12 : 1),
      PageUp: () => sameDayInMonth(key, event.shiftKey ? -12 : -1),
    };
    if (moves[event.key]) {
      event.preventDefault();
      moveTo(moves[event.key]());
    }
  };

  const canGoBack = monthKey > minMonthKey;
  const anyOpen = weeks.flat().some((key) => key && statusOf(key).open);
  const currentInMonth = currentKey && monthOf(currentKey) === monthKey;

  return (
    <Box>
      <Flex align="center" justify="space-between" mb={2}>
        <Flex align="center" gap={2}>
          <Text id={titleId} fontWeight="semibold" color="gray.800" aria-live="polite">
            {monthTitle(monthKey)}
          </Text>
          {loading && <Spinner size="xs" color="gray.400" label="Loading open times" />}
        </Flex>
        <Flex gap={1}>
          <IconButton
            size="sm"
            variant="ghost"
            icon={<FiChevronLeft />}
            aria-label="Previous month"
            isDisabled={!canGoBack}
            onClick={() => onMonthChange(addMonths(monthKey, -1))}
          />
          <IconButton
            size="sm"
            variant="ghost"
            icon={<FiChevronRight />}
            aria-label="Next month"
            onClick={() => onMonthChange(addMonths(monthKey, 1))}
          />
        </Flex>
      </Flex>

      <Box role="grid" aria-labelledby={titleId} aria-busy={loading || undefined}>
        <Box role="row" display="grid" gridTemplateColumns="repeat(7, minmax(0, 1fr))" gap={1} mb={1}>
          {WEEKDAYS.map((weekday) => (
            <Box key={weekday.long} role="columnheader" aria-label={weekday.long} textAlign="center" fontSize="xs" color="gray.500" py={1}>
              {weekday.short}
            </Box>
          ))}
        </Box>
        {weeks.map((week, row) => (
          <Box key={row} role="row" display="grid" gridTemplateColumns="repeat(7, minmax(0, 1fr))" gap={1}>
            {week.map((key, column) => {
              if (!key) return <Box key={`empty-${column}`} role="gridcell" aria-hidden="true" />;
              const status = statusOf(key);
              const selected = key === selectedKey;
              const isToday = key === todayKey;
              const isCurrent = key === currentKey;
              const copy = status.reason ? closedCopyFor(status.reason) : null;
              const extra = [isToday ? "today" : null, isCurrent ? "your current appointment" : null].filter(Boolean).join(", ");
              const label = status.open
                ? `${longDay(key)}, ${status.count} open ${status.count === 1 ? "time" : "times"}${extra ? `, ${extra}` : ""}`
                : copy
                  ? `${longDay(key)}${extra ? `, ${extra}` : ""}. ${copy.title}. ${copy.detail}`
                  : `${longDay(key)}, loading`;
              const kind = status.open ? "open" : status.full ? "full" : "none";
              const cell = (
                <Button
                  ref={(node) => (node ? cellRefs.current.set(key, node) : cellRefs.current.delete(key))}
                  data-date={key}
                  aria-label={label}
                  aria-disabled={status.open ? undefined : true}
                  tabIndex={key === tabbableKey ? 0 : -1}
                  onClick={() => pick(key)}
                  onKeyDown={(event) => onKeyDown(event, key)}
                  onFocus={() => setFocusedKey(key)}
                  variant="unstyled"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  gap="3px"
                  w="100%"
                  minW="44px"
                  h="44px"
                  borderRadius="md"
                  fontSize="sm"
                  fontWeight={status.open ? "semibold" : "normal"}
                  color={selected ? "white" : status.open ? "gray.800" : isToday ? "gray.700" : key < todayKey ? "gray.400" : "gray.500"}
                  bg={selected ? "blue.600" : isToday ? "gray.50" : "transparent"}
                  boxShadow={isCurrent && !selected ? "inset 0 0 0 1.5px var(--chakra-colors-gray-400)" : "none"}
                  cursor={status.open ? "pointer" : "default"}
                  transition="background-color 120ms"
                  _hover={status.open ? {bg: selected ? "blue.700" : "blue.50"} : {}}
                  // A day without times is still a date to read: Chakra fades every
                  // aria-disabled button to 40%, which left the numbers illegible.
                  _disabled={{opacity: 1, cursor: "default"}}
                  _focusVisible={{outline: "none", boxShadow: "0 0 0 3px var(--chakra-colors-blue-300)"}}
                  sx={{fontVariantNumeric: "tabular-nums"}}
                >
                  <span>{Number(key.slice(8, 10))}</span>
                  <Mark kind={kind} onSelected={selected} />
                </Button>
              );
              return (
                <Box key={key} role="gridcell" aria-selected={selected}>
                  {copy && !status.open ? (
                    <Tooltip label={copy.title} placement="top" hasArrow openDelay={150} fontSize="xs">
                      {cell}
                    </Tooltip>
                  ) : (
                    cell
                  )}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>

      <Flex gap={4} mt={2} fontSize="xs" color="gray.600" wrap="wrap">
        <Flex align="center" gap={1.5}>
          <Box w="5px" h="5px" borderRadius="full" bg="blue.500" />
          Open times
        </Flex>
        {currentInMonth && (
          <Flex align="center" gap={1.5}>
            {/* The day itself, outlined as it is in the grid — a square alone read as a checkbox. */}
            <Box
              aria-hidden="true"
              px={1}
              lineHeight="18px"
              borderRadius="sm"
              boxShadow="inset 0 0 0 1.5px var(--chakra-colors-gray-400)"
              color="gray.700"
              fontWeight="semibold"
              sx={{fontVariantNumeric: "tabular-nums"}}
            >
              {Number(currentKey.slice(8, 10))}
            </Box>
            Your current appointment
          </Flex>
        )}
        {loaded && !anyOpen && !loading && <Text>No open times this month.</Text>}
      </Flex>
    </Box>
  );
}
